import React, { useState } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { Tooltip as ReactTooltip } from 'react-tippy'

import refreshIcon from 'styles/assets/tableIcons/refresh.svg'
import refreshGrayIcon from 'styles/assets/tableIcons/refresh_gray.svg'
import checkIcon from 'styles/assets/check_green.svg'
import { fonts, mediaQueries, palette, shadows } from 'styles/variables'
import { chooseTag, getMetricOption, isUpdatedNow } from 'utils/functions'
import { pushWithLanguageRoutes, translate } from 'utils/translation'
import Tooltip from 'components/tooltip/Tooltip'
import {
  LoadingResponsive,
  TooltipLoadingIndicator,
} from 'components/loading/Loading'
import { Text } from 'components/topography'
import { IconButton } from 'components/buttons/Buttons'
import Icon from 'components/icon/Icon'
import { usePrevious } from 'utils/hooks'
import { Mixpanel } from 'utils/mixpanel'

import MultiInfoBox from './MultiInfoBox'

export const BoxWrapper = styled.div(
  ({ mobileStyles, isGridLayout, borderTop }) => ({
    display: isGridLayout ? 'grid' : 'flex',
    gridTemplateColumns: '50% 1fr 1fr',
    gridTemplateRows: 'auto auto',
    gridGap: '30px',
    width: '100%',
    paddingTop: borderTop ? '20px' : 0,
    borderTop: borderTop ? `1px solid ${palette.lightGray50}` : 'none',
    [mediaQueries.mobile]: {
      display: 'block',
      ...(mobileStyles || {}),
    },
    [mediaQueries.smallDesktop]: {
      gridTemplateRows: 'repeat(3, auto)',
      gridTemplateColumns: '50% 50%',
    },
  }),
)

const getCursor = (disabled, noHover) => {
  if (disabled) return 'not-allowed'
  if (noHover) return 'default'
  return 'point'
}

export const Box = styled.div(
  ({ disabled, noHover, isGridLayout, mobileStyles }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    border: `1px solid ${palette.lightGray50}`,
    backgroundColor: palette.white,
    flex: '1',
    minHeight: '100px',
    color: disabled ? palette.gray : 'inherit',
    cursor: getCursor(disabled, noHover),
    '&.active': {
      cursor: 'pointer',
    },
    ':nth-child(n)': {
      margin: isGridLayout ? 0 : '0 10px 0 10px',
      padding: '20px 25px',
    },
    ':first-child': {
      margin: isGridLayout ? 0 : '0 10px 0px 0',
    },
    ':last-child': {
      margin: isGridLayout ? 0 : '0 0 0px 10px',
    },
    '&:hover span': {
      color: !disabled ? palette.orange : palette.gray,
    },
    [!noHover && !disabled && '&:hover']: {
      boxShadow: shadows.shadowB,
      transform: 'scale(1.1)',
      zIndex: 99,
    },
    [mediaQueries.mobile]: {
      justifyContent: 'center',
      ':nth-child(n)': {
        margin: '10px 0 0 0',
        padding: '20px 20px',
        display: 'flex',
      },
      ...(mobileStyles || {}),
    },
  }),
)

export const BoxData = styled.div(({ uppercase, isGridLayout }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '10px',
  textTransform: uppercase ? 'uppercase' : 'inherits',
  ':first-child': {
    fontSize: '14px',
    fontFamily: fonts.primaryBold,
    margin: '0 0 10px 0',
    '> span': {
      fontSize: '14px',
      fontFamily: fonts.primaryBold,
      cursor: 'pointer',
    },
  },
  ':nth-child(2)': {
    alignItems: 'center',
    fontSize: '24px',
    fontFamily: fonts.primaryBold,
    '> span': {
      fontSize: '24px',
      fontFamily: fonts.primaryBold,
      cursor: 'pointer',
    },
  },
  ':nth-child(3)': {
    flex: 1,
    fontSize: '14px',
    letterSpacing: '0.05em',
    lineHeight: '1.4em',
    whiteSpace: 'pre-line',
  },
  [mediaQueries.mobile]: {
    ':nth-child(n)': {
      justifyContent: 'center',
    },
    ':first-child': {
      '> span': {
        fontSize: '12px',
      },
    },
    ':nth-child(2)': {
      flexDirection: isGridLayout ? 'column' : 'row',
      alignItems: 'center',
      '> span': {
        fontSize: '20px',
      },
      marginBottom: '10px',
    },
    ':nth-child(3)': {
      fontSize: '12px',
    },
  },
}))

export const BoxMessageContent = styled.div(
  ({ bgColor, borderColor, customStyles, mobileMargin }) => ({
    backgroundColor: bgColor,
    border: `1px solid ${borderColor}`,
    padding: '6px 8px 4px 8px',
    margin: '0 0 3px 15px',
    borderRadius: '2px',
    color: palette.darkGray,
    fontWeight: 'normal',
    fontSize: '12px',
    fontFamily: fonts.primaryMedium,
    ...customStyles,
    [mediaQueries.mobile]: {
      margin: mobileMargin || '0 0 0 15px',
    },
  }),
)

export const BoxMessage = ({
  type,
  value,
  style,
  customStyles,
  isGridLayout,
}) => {
  const label = chooseTag(type, value)
  if (label) {
    return (
      <BoxMessageContent
        data-testid="box-message"
        bgColor={label.bgColor}
        borderColor={label.borderColor}
        style={style}
        customStyles={customStyles}
        mobileMargin={isGridLayout ? '10px 0px' : null}
      >
        {translate(label.tag).toUpperCase()}
      </BoxMessageContent>
    )
  }
  return null
}

const goToDetail = (url) => {
  if (url) {
    pushWithLanguageRoutes(url)
  }
}

const Boxes = ({
  borderTop,
  style,
  wrapperMobileStyle,
  isGridLayout,
  boxes,
  boxMobileStyle,
  isLoading,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 })
  return (
    <BoxWrapper
      data-testid="overview-boxes-div"
      borderTop={borderTop}
      style={style}
      mobileStyles={wrapperMobileStyle}
      isGridLayout={isGridLayout}
    >
      {boxes.map((item) => (
        <React.Fragment key={`boxWith${item.title}`}>
          {item.type === 'multi' ? (
            <MultiInfoBox
              title={item.title}
              subtitle={item.subtitle}
              description={item.description}
              leftSideInformation={item.value.leftSide}
              rightSideContent={item.value.rightSide}
            />
          ) : (
            <Box
              data-testid="overview-box"
              onClick={() => {
                const metricSelected = getMetricOption(item.title)
                Mixpanel.trackMetricClick(metricSelected)

                !item.disabled &&
                  !(isMobile && item.mobileClickDisabled) &&
                  goToDetail(item.url)
              }}
              mobileStyles={boxMobileStyle}
              disabled={item.disabled}
              isGridLayout={isGridLayout}
            >
              <ReactTooltip
                disabled={!item.disabled}
                arrow
                position="top"
                html={
                  <Text
                    fontSize="12px"
                    lineHeight="16px"
                    color={palette.white}
                    textAlign="left"
                    style={{ width: 200 }}
                    boldColor={palette.white}
                    dangerouslySetInnerHTML={{
                      __html: item.disableTooltip,
                    }}
                  />
                }
                style={{
                  margin: item.disabled ? '-20px -25px' : '0',
                  padding: item.disabled ? '20px 25px' : '0',
                }}
              >
                <BoxData isGridLayout={isGridLayout} data-testid="box-title">
                  {item.url ? (
                    <span>{item.title.toUpperCase()}</span>
                  ) : (
                    item.title.toUpperCase()
                  )}
                  {item.description && (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div
                      data-testid="tooltip-container"
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <Tooltip
                        id={item.title}
                        offset={item.offset}
                        text={item.description}
                        place={item.descriptionPosition}
                        style={{ marginLeft: '10px' }}
                      />
                    </div>
                  )}
                </BoxData>
                {isLoading ? (
                  <BoxData
                    style={{ height: '33px' }}
                    isGridLayout={isGridLayout}
                  >
                    <LoadingResponsive size="30px" />
                  </BoxData>
                ) : (
                  <>
                    <BoxData isGridLayout={isGridLayout}>
                      {item.url && item.value !== '_' ? (
                        <span>{item.value}</span>
                      ) : (
                        item.value
                      )}
                      {!item.noLabel && (
                        <BoxMessage
                          type={item.type}
                          value={item.numberValue}
                          isGridLayout={isGridLayout}
                        />
                      )}
                      {item.cta && item.cta()}
                    </BoxData>
                    {item.cta_status}
                    {item.subtitle && (
                      <BoxData
                        style={{
                          margin: item.subtitle == null ? '0' : '10px 0 0 0',
                        }}
                        isGridLayout={isGridLayout}
                      >
                        {item.subtitle}
                      </BoxData>
                    )}
                  </>
                )}
              </ReactTooltip>
            </Box>
          )}
        </React.Fragment>
      ))}
    </BoxWrapper>
  )
}

const SDUpdateBoxWrapper = styled.div((props) => ({
  margin: props.margin || '0 0 0 14px',
  [mediaQueries.mobile]: {
    margin: '0 0 0 7px',
  },
}))

export const SDUpdateBox = ({
  isLoading,
  isPending,
  updatedAt,
  onUpdate,
  ...props
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const prevIsLoading = usePrevious(isLoading)

  if (isLoading) {
    return (
      <SDUpdateBoxWrapper
        margin={isLoading ? '0 0 4px 14px' : '0 0 0 7px'}
        {...props}
      >
        <TooltipLoadingIndicator
          position="left"
          show
          tooltipContent={
            <Text
              fontSize="12px"
              lineHeight="16px"
              color={palette.white}
              textAlign="left"
              style={{ width: 200 }}
              dangerouslySetInnerHTML={{
                __html: translate('fetching_sd_scores'),
              }}
            />
          }
        />
      </SDUpdateBoxWrapper>
    )
  }
  return (
    <SDUpdateBoxWrapper margin={isLoading ? '0 0 4px 14px' : '0 0 0 7px'}>
      {isUpdatedNow(updatedAt) ? (
        <Icon
          src={checkIcon}
          width="18px"
          height="12px"
          margin="0"
          className={!isLoading && prevIsLoading ? 'fadeOut' : ''}
        />
      ) : (
        <ReactTooltip
          position="top-end"
          arrow
          theme="dark"
          open={tooltipVisible}
          boundary="viewport"
          title={translate('refresh_metrics_tooltip')}
        >
          <IconButton
            icon={isPending ? refreshGrayIcon : refreshIcon}
            iconWidth="20px"
            iconHeight="20px"
            className={isPending ? '!tw-cursor-default' : ''}
            onMouseEnter={() => {
              if (!isPending) {
                setTooltipVisible(true)
              }
            }}
            onMouseLeave={() => {
              setTooltipVisible(false)
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              if (isPending) return

              onUpdate()
            }}
          />
        </ReactTooltip>
      )}
    </SDUpdateBoxWrapper>
  )
}

export default Boxes
